import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { TourRouteState, Waypoint } from './tour-route-state';
import { MapProvider } from '../map/map-provider';
import { ViewModeProvider } from '../map/view-mode-provider';
import { ViewMode } from '../../../../shared/enums/view-mode.enum';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import PointSymbol3D from '@arcgis/core/symbols/PointSymbol3D';
import IconSymbol3DLayer from '@arcgis/core/symbols/IconSymbol3DLayer';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import Color from '@arcgis/core/Color';

let _seq = 0;

@Injectable()
export class TourRouteNotifier extends BaseNotifier<TourRouteState> {
  private static readonly INITIAL: TourRouteState = {
    active: false,
    waypoints: [],
    currentIndex: -1,
    playing: false,
    paused: false,
    speed: 'normal',
  };

  private clickHandle: { remove(): void } | null = null;
  private viewWatcherHandle: { destroy(): void } | null = null;
  private lastViewForClick: MapView | SceneView | null = null;
  private routeLayer: GraphicsLayer | null = null;

  private orbitActive = false;
  private orbitFrameId = 0;
  private tourTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(ref: Ref) {
    super(ref, TourRouteNotifier.INITIAL);
  }

  private get mapProvider(): MapProvider {
    return this.ref.read(MapProvider);
  }

  private get viewModeProvider(): ViewModeProvider {
    return this.ref.read(ViewModeProvider);
  }

  private get view(): MapView | SceneView | null {
    return this.mapProvider.notifier.getActiveView();
  }

  private get is3d(): boolean {
    return this.viewModeProvider.state().mode === ViewMode.Scene;
  }

  // ── Public API ──────────────────────────────────────────────

  async toggle(): Promise<void> {
    if (this.snapshot().active) {
      this.deactivate();
    } else {
      await this.activate();
    }
  }

  async activate(): Promise<void> {
    if (this.snapshot().active) return;
    this.setState({ ...TourRouteNotifier.INITIAL, active: true, waypoints: this.snapshot().waypoints });
    this.ensureLayer();
    this.drawAll();

    if (!this.is3d) {
      this.mapProvider.notifier.switchTo(ViewMode.Scene);
      this.viewModeProvider.notifier.setMode(ViewMode.Scene);
    }

    await this.waitForView();
    this.attachClickHandler();
    this.startViewWatcher();
  }

  deactivate(): void {
    this.stopAll();
    this.detachClickHandler();
    this.stopViewWatcher();
    this.clearGraphics();
    this.lastViewForClick = null;
    this.setState({ ...TourRouteNotifier.INITIAL });
  }

  addWaypoint(lon: number, lat: number): void {
    const wp: Waypoint = { id: `wp-${++_seq}`, lon, lat, label: `${this.snapshot().waypoints.length + 1}` };
    this.updateState(s => ({ ...s, waypoints: [...s.waypoints, wp] }));
    this.drawAll();
  }

  removeWaypoint(id: string): void {
    this.updateState(s => {
      const newWps = s.waypoints.filter(w => w.id !== id);
      let newIdx = s.currentIndex;
      if (newIdx >= newWps.length) newIdx = newWps.length - 1;
      return { ...s, waypoints: newWps, currentIndex: newIdx };
    });
    this.drawAll();
  }

  clearWaypoints(): void {
    this.stopAll();
    this.updateState(s => ({ ...s, waypoints: [], currentIndex: -1, paused: false }));
    this.drawAll();
  }

  goToWaypoint(index: number): void {
    const wps = this.snapshot().waypoints;
    if (index < 0 || index >= wps.length) return;
    this.updateState(s => ({ ...s, currentIndex: index }));
    this.drawAll();
    this.flyAndOrbit(wps[index]);
  }

  next(): void {
    const s = this.snapshot();
    const nextIdx = s.currentIndex + 1;
    if (nextIdx >= s.waypoints.length) {
      this.stop();
      return;
    }
    this.goToWaypoint(nextIdx);
  }

  prev(): void {
    const s = this.snapshot();
    if (s.currentIndex <= 0) return;
    this.goToWaypoint(s.currentIndex - 1);
  }

  play(): void {
    const s = this.snapshot();
    if (s.waypoints.length === 0) return;
    this.stopAll();

    if (s.paused && s.currentIndex >= 0) {
      this.updateState(st => ({ ...st, playing: true, paused: false }));
      this.resumeOrbitStep(s.currentIndex);
    } else {
      this.updateState(st => ({ ...st, playing: true, paused: false }));
      const startIdx = s.currentIndex < 0 ? 0 : Math.min(s.currentIndex, s.waypoints.length - 1);
      this.tourStep(startIdx);
    }
  }

  pause(): void {
    this.stopAll();
    this.updateState(s => ({ ...s, playing: false, paused: true }));
  }

  stop(): void {
    this.stopAll();
    this.updateState(s => ({ ...s, playing: false, paused: false, currentIndex: -1 }));
    this.drawAll();
  }

  setSpeed(speed: 'slow' | 'normal' | 'fast'): void {
    this.updateState(s => ({ ...s, speed }));
  }

  // ── Tour step: fly + smooth full-revolution orbit ───────────

  private tourStep(index: number): void {
    const s = this.snapshot();
    if (!s.playing) return;
    if (index >= s.waypoints.length) {
      this.stop();
      return;
    }

    this.updateState(st => ({ ...st, currentIndex: index }));
    this.drawAll();

    const wp = s.waypoints[index];

    this.flyToForTour(wp, () => {
      if (!this.snapshot().playing) return;

      this.smoothOrbitRevolution(wp, () => {
        if (!this.snapshot().playing) return;
        this.tourTimer = setTimeout(() => this.tourStep(index + 1), this.pauseBetweenPoints);
      });
    });
  }

  private resumeOrbitStep(index: number): void {
    const s = this.snapshot();
    if (!s.playing) return;
    if (index >= s.waypoints.length) { this.stop(); return; }

    this.updateState(st => ({ ...st, currentIndex: index }));
    this.drawAll();

    const wp = s.waypoints[index];
    const sv = this.getSceneView();
    if (!sv) return;

    const SR = { spatialReference: { wkid: 4326 } };
    const targetPoint = new Point({ x: wp.lon, y: wp.lat, z: 0, ...SR });
    const point = new Point({ x: wp.lon, y: wp.lat, ...SR });

    (sv as any).map.ground.queryElevation(point)
      .then((res: any) => {
        const elev = Math.max(res?.geometry?.z || 0, 0);
        targetPoint.z = elev + 80;
        this.runSmoothOrbit(sv, targetPoint, 360, () => {
          if (!this.snapshot().playing) return;
          this.tourTimer = setTimeout(() => this.tourStep(index + 1), this.pauseBetweenPoints);
        });
      })
      .catch(() => {
        targetPoint.z = 80;
        this.runSmoothOrbit(sv, targetPoint, 360, () => {
          if (!this.snapshot().playing) return;
          this.tourTimer = setTimeout(() => this.tourStep(index + 1), this.pauseBetweenPoints);
        });
      });
  }

  private flyToForTour(wp: Waypoint, onDone: () => void): void {
    const v = this.view;
    if (!v || v.type !== '3d') { onDone(); return; }
    const sv = v as any;
    const SR = { spatialReference: { wkid: 4326 } };
    const point = new Point({ x: wp.lon, y: wp.lat, ...SR });

    sv.map.ground.queryElevation(point)
      .then((res: any) => {
        const elev = Math.max(res?.geometry?.z || 0, 0);
        return sv.goTo(
          {
            target: new Point({ x: wp.lon, y: wp.lat, z: elev + 450, ...SR }),
            heading: 0,
            tilt: 60,
            zoom: 16,
          },
          { duration: this.flightDuration, easing: 'in-out-expo' as any },
        );
      })
      .then(() => onDone())
      .catch(() => onDone());
  }

  // ── Smooth orbit via requestAnimationFrame + goTo(no animate)
  // Pattern from Esri: https://developers.arcgis.com/javascript/latest/sample-code/scene-goto/
  // Uses incremental heading updates at ~20fps for buttery smooth rotation.

  /**
   * Complete 360° revolution around a waypoint (tour mode).
   * Uses requestAnimationFrame for smooth continuous rotation.
   */
  private smoothOrbitRevolution(wp: Waypoint, onDone: () => void): void {
    const sv = this.getSceneView();
    if (!sv) { onDone(); return; }

    const SR = { spatialReference: { wkid: 4326 } };
    const point = new Point({ x: wp.lon, y: wp.lat, ...SR });

    (sv as any).map.ground.queryElevation(point)
      .then((res: any) => {
        const elev = Math.max(res?.geometry?.z || 0, 0);
        const targetPoint = new Point({ x: wp.lon, y: wp.lat, z: elev + 80, ...SR });
        this.runSmoothOrbit(sv, targetPoint, 360, onDone);
      })
      .catch(() => {
        const targetPoint = new Point({ x: wp.lon, y: wp.lat, z: 80, ...SR });
        this.runSmoothOrbit(sv, targetPoint, 360, onDone);
      });
  }

  /**
   * Infinite orbit around a waypoint (single-click mode).
   * Runs until stopOrbit() is called.
   */
  private startContinuousOrbit(wp: Waypoint): void {
    const sv = this.getSceneView();
    if (!sv) return;

    const SR = { spatialReference: { wkid: 4326 } };
    const point = new Point({ x: wp.lon, y: wp.lat, ...SR });

    (sv as any).map.ground.queryElevation(point)
      .then((res: any) => {
        const elev = Math.max(res?.geometry?.z || 0, 0);
        const targetPoint = new Point({ x: wp.lon, y: wp.lat, z: elev + 80, ...SR });
        this.runSmoothOrbit(sv, targetPoint, Infinity, undefined);
      })
      .catch(() => {
        const targetPoint = new Point({ x: wp.lon, y: wp.lat, z: 80, ...SR });
        this.runSmoothOrbit(sv, targetPoint, Infinity, undefined);
      });
  }

  /**
   * Core smooth orbit engine.
   * @param sv        SceneView instance
   * @param target    Fixed orbit center point (with z offset)
   * @param totalDeg  Total degrees to rotate (360 for revolution, Infinity for continuous)
   * @param onDone    Callback when revolution completes (null for infinite)
   */
  private runSmoothOrbit(
    sv: any,
    target: Point,
    totalDeg: number,
    onDone: (() => void) | undefined,
  ): void {
    this.orbitActive = true;
    const startHeading: number = sv.camera?.heading ?? 0;
    const degPerSec = this.orbitDegreesPerSecond;
    const frameInterval = 50; // 20fps — smooth enough, avoids hammering the API
    let startTime = 0;
    let lastFrameTime = 0;

    const animate = (now: number): void => {
      if (!this.orbitActive) return;

      if (startTime === 0) {
        startTime = now;
        lastFrameTime = now;
      }

      // Throttle to frameInterval
      if (now - lastFrameTime < frameInterval) {
        this.orbitFrameId = requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = now;

      const elapsedSec = (now - startTime) / 1000;
      const traveled = elapsedSec * degPerSec;

      if (totalDeg !== Infinity && traveled >= totalDeg) {
        // Revolution complete — snap to exact final heading
        sv.goTo(
          { target, heading: (startHeading + totalDeg) % 360, tilt: 60, zoom: 16 },
          { animate: false },
        ).catch(() => {}).then(() => {
          this.orbitActive = false;
          onDone?.();
        });
        return;
      }

      const heading = (startHeading + traveled) % 360;

      sv.goTo(
        { target, heading, tilt: 60, zoom: 16 },
        { animate: false },
      ).catch(() => {
        this.orbitActive = false;
      });

      this.orbitFrameId = requestAnimationFrame(animate);
    };

    this.orbitFrameId = requestAnimationFrame(animate);
  }

  // ── Single click: fly + infinite smooth orbit ───────────────

  private async flyAndOrbit(wp: Waypoint): Promise<void> {
    const sv = this.getSceneView();
    if (!sv) return;

    this.stopOrbit();
    const SR = { spatialReference: { wkid: 4326 } };
    const point = new Point({ x: wp.lon, y: wp.lat, ...SR });

    let elev = 0;
    try {
      const result = await (sv as any).map.ground.queryElevation(point);
      elev = Math.max(result?.geometry?.z || 0, 0);
    } catch { /* 0 */ }

    try {
      await sv.goTo(
        {
          target: new Point({ x: wp.lon, y: wp.lat, z: elev + 450, ...SR }),
          heading: 0,
          tilt: 60,
          zoom: 16,
        },
        { duration: this.flightDuration, easing: 'in-out-expo' as any },
      );
    } catch { /* ignore */ }

    this.startContinuousOrbit(wp);
  }

  // ── Stop orbit ──────────────────────────────────────────────

  private stopOrbit(): void {
    this.orbitActive = false;
    if (this.orbitFrameId) {
      cancelAnimationFrame(this.orbitFrameId);
      this.orbitFrameId = 0;
    }
    const v = this.view;
    if (v && v.type === '3d') {
      (v as any).goTo((v as any).camera, { animate: false }).catch(() => {});
    }
  }

  private stopAll(): void {
    this.stopOrbit();
    if (this.tourTimer) {
      clearTimeout(this.tourTimer);
      this.tourTimer = null;
    }
  }

  // ── Helpers ─────────────────────────────────────────────────

  private getSceneView(): SceneView | null {
    const v = this.view;
    if (!v || v.type !== '3d') return null;
    return v as SceneView;
  }

  // ── Timing ──────────────────────────────────────────────────

  private get flightDuration(): number {
    return this.speedConfig.flight;
  }

  /** Degrees per second for orbit rotation. */
  private get orbitDegreesPerSecond(): number {
    return this.speedConfig.degPerSec;
  }

  private get pauseBetweenPoints(): number {
    return this.speedConfig.pause;
  }

  private get speedConfig() {
    const s = this.snapshot().speed;
    switch (s) {
      case 'slow':
        // 360° in ~30s at 12°/s — cinematic
        return { flight: 6000, degPerSec: 12, pause: 1500 };
      case 'fast':
        // 360° in ~9s at 40°/s — dynamic
        return { flight: 2000, degPerSec: 40, pause: 500 };
      default: // normal
        // 360° in ~18s at 20°/s — balanced
        return { flight: 3500, degPerSec: 20, pause: 1000 };
    }
  }

  // ── Wait for view ───────────────────────────────────────────

  private waitForView(): Promise<void> {
    return new Promise(resolve => {
      const check = () => {
        const v = this.view;
        if (v && v.type === '3d') { resolve(); return; }
        setTimeout(check, 100);
      };
      check();
    });
  }

  // ── Click handling ──────────────────────────────────────────

  private attachClickHandler(): void {
    this.detachClickHandler();
    const v = this.view;
    if (!v) return;
    this.lastViewForClick = v;
    this.attachClickHandlerToView(v);
  }

  private attachClickHandlerToView(v: MapView | SceneView): void {
    const handle = v.on('click', (event: any) => {
      if (!this.snapshot().active) return;
      const mp = event.mapPoint;
      if (!mp) return;
      const lon = mp.longitude;
      const lat = mp.latitude;
      if (lon == null || lat == null) return;
      this.addWaypoint(lon, lat);
    });
    this.clickHandle = handle;
  }

  private detachClickHandler(): void {
    if (this.clickHandle) {
      this.clickHandle.remove();
      this.clickHandle = null;
    }
  }

  // ── View watcher ────────────────────────────────────────────

  private startViewWatcher(): void {
    this.stopViewWatcher();
    this.viewWatcherHandle = this.listen(() => {
      const v = this.view;
      if (v && v !== this.lastViewForClick && this.snapshot().active) {
        this.detachClickHandler();
        this.lastViewForClick = v;
        this.attachClickHandlerToView(v);
      }
    });
  }

  private stopViewWatcher(): void {
    if (this.viewWatcherHandle) {
      this.viewWatcherHandle.destroy();
      this.viewWatcherHandle = null;
    }
  }

  // ── Graphics layer ──────────────────────────────────────────

  private ensureLayer(): void {
    if (this.routeLayer) return;
    const map = this.mapProvider.state().map;
    if (!map) return;

    this.routeLayer = new GraphicsLayer({
      id: 'tour-route-layer',
      title: 'Recorrido',
      listMode: 'hide',
      elevationInfo: { mode: 'on-the-ground' as const },
    });
    map.add(this.routeLayer);
  }

  private drawAll(): void {
    if (!this.routeLayer) return;
    this.routeLayer.removeAll();

    const wps = this.snapshot().waypoints;
    if (wps.length === 0) return;

    const SR = { spatialReference: { wkid: 4326 } };
    const currentIdx = this.snapshot().currentIndex;

    if (wps.length >= 2) {
      const coords = wps.map(w => [w.lon, w.lat]);
      this.routeLayer.add(new Graphic({
        geometry: new Polyline({ paths: [coords], ...SR }),
        symbol: new SimpleLineSymbol({
          color: [0, 200, 255, 0.7],
          width: 3,
          style: 'solid',
        }),
      }));
    }

    wps.forEach((wp, i) => {
      const isCurrent = i === currentIdx;
      const isStart = i === 0;
      const isEnd = i === wps.length - 1 && wps.length > 1;

      let color: number[];
      let size: number;
      if (isCurrent) { color = [255, 107, 53, 1]; size = 24; }
      else if (isStart) { color = [34, 197, 94, 1]; size = 18; }
      else if (isEnd) { color = [239, 68, 68, 1]; size = 18; }
      else { color = [0, 200, 255, 0.9]; size = 16; }

      this.routeLayer!.add(new Graphic({
        geometry: new Point({ longitude: wp.lon, latitude: wp.lat, z: 20, ...SR }),
        symbol: new PointSymbol3D({
          symbolLayers: [
            new IconSymbol3DLayer({
              resource: { primitive: 'circle' },
              material: { color },
              outline: { color: [255, 255, 255, 0.9], size: 2 },
              size,
            }),
          ],
        } as any),
        attributes: { index: i },
      }));

      this.routeLayer!.add(new Graphic({
        geometry: new Point({ longitude: wp.lon, latitude: wp.lat, z: 40, ...SR }),
        symbol: new TextSymbol({
          text: `${i + 1}`,
          color: new Color([255, 255, 255]),
          haloColor: new Color([0, 0, 0, 0.75]),
          haloSize: 2,
          font: { size: 10, weight: 'bold' },
          yoffset: -12,
        }),
        attributes: { index: i },
      }));
    });
  }

  private clearGraphics(): void {
    if (this.routeLayer) this.routeLayer.removeAll();
  }

  // ── Lifecycle ───────────────────────────────────────────────

  protected override onDispose(): void {
    this.stopAll();
    this.detachClickHandler();
    this.stopViewWatcher();
    this.clearGraphics();
    this.lastViewForClick = null;
    if (this.routeLayer) {
      try { this.routeLayer.destroy(); } catch {}
      this.routeLayer = null;
    }
  }
}
